import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Globe } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import ProductCard from './ProductCard';
import PricingCard from './ui/pricing-card';
import { getProductsByCategory } from '../data/workspaceData';
import { Button } from './ui/button';

const VirtualOfficePage = () => {
  const navigate = useNavigate();
  const products = getProductsByCategory('virtual-office');

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Section 1: Hero Section */}
      <div className="bg-gradient-to-br from-gray-50 via-white to-gray-100 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-12">
          {/* Back Navigation */}
          <button 
            onClick={() => navigate('/products')}
            className="flex items-center space-x-2 text-[#e30613] hover:text-[#c1050f] mb-8 transition-colors"
          >
            <ArrowLeft size={20} />
            <span className="font-medium">Back to All Products</span>
          </button>

          {/* Hero Content */}
          <div className="text-center mb-12">
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-[#e30613] rounded-2xl shadow-lg">
                <Globe className="w-12 h-12 text-white" strokeWidth={1.5} />
              </div>
            </div>
            <h1 className="text-5xl lg:text-6xl font-black text-gray-900 mb-6 leading-tight">
              VIRTUAL OFFICE
            </h1>
            <p className="text-xl text-gray-700 leading-relaxed max-w-4xl mx-auto">
              A virtual office provides your business an address at any of our office locations worldwide, enabling you to build an instant presence, without the costs of a dedicated office. We can handle your mail and forward it on, take your calls, and you can easily book a meeting room or office when you're in town.
            </p>
          </div>
        </div>
      </div>

      {/* Section 2: Available Virtual Office Options */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4 text-center">
              Available Virtual Office Options
            </h2>
            <p className="text-gray-600 text-center text-lg">
              Choose from our range of virtual office solutions designed to meet your business needs.
            </p>
          </div>

          {products.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {products.map((product) => (
                <ProductCard 
                  key={product.id}
                  product={product}
                  category={{ title: 'Virtual Office', icon: 'Globe' }}
                  className="h-full"
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <Globe className="w-16 h-16 text-gray-400 mx-auto mb-4" strokeWidth={1} />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                No products available
              </h3>
              <p className="text-gray-600 mb-6">
                We're currently updating our virtual office options. Please check back soon.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Section 3: Enhanced Business Identity */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center max-w-5xl mx-auto">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              ENHANCE YOUR BUSINESS IDENTITY WITH<br />LAKSH SPACE VIRTUAL OFFICE
            </h2>
            <div className="space-y-4 text-lg text-gray-700 leading-relaxed">
              <p>
                At Laksh Space, we understand that not every business requires a full-time physical office. Our Virtual Office solutions help you establish a professional presence with a premium business address — without the cost or commitment of traditional office space.
              </p>
              <p>
                Whether you're a startup, freelancer, or growing company, a virtual office gives you the flexibility to operate from anywhere while maintaining a credible business image. Access meeting spaces and office facilities whenever needed — all managed seamlessly by our team.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Section 4: Pricing Cards */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-[#e30613] mb-6">
              Choose Your Virtual Office Plan
            </h2>
            <p className="text-gray-700 text-lg leading-relaxed max-w-4xl mx-auto">
              Select the plan that best fits your business needs and budget. All plans include professional business address services with flexible terms and transparent pricing.
            </p>
          </div>

          {/* Pricing Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Company Registration Plan */}
            <PricingCard 
              icon={
                <img src="./images-optimized/galery-images/1.png" alt="Company Registration" className="w-12 h-12 object-contain" />
              }
              title="Company Registration Plan"
              price="₹  15,599"
              originalPrice="₹ 13,599"
              period="/per year + GST"
              discountText="(when you sign up for 2 years)"
              buttonText="Choose Plans"
              features={[
                "Ideal for: New Company, New Address, etc.",
                "Update Your Registered Address Officially",
                "Use the Address for Opening a Bank Account",
                "Use Address for GST Certification",
                "On-site GST Verification",
                "Courier Receiving and Forwarding Services",
                "Use Professional Address Across All Platforms Effectively",
                "Access Meeting Rooms as Per Requirements",
                "No Hidden Charges"
              ]}
            />
            
            {/* GST Registration Plan */}
            <PricingCard 
              icon={
                <img src="./images-optimized/galery-images/2.png" alt="GST Registration" className="w-12 h-12 object-contain" />
              }
              title="GST Registration Plan"
              price="₹ 11,999"
              originalPrice="₹ 9,999"
              period="/per year + GST"
              discountText="(when you sign up for 2 years)"
              buttonText="Choose Plans"
              features={[
                "Ideal for: GST registration and business operations",
                "Update Your Registered Address Officially",
                "Use the Address for Opening a Bank Account",
                "Use Address for GST",
                "On-site GST Verification",
                "Courier Receiving and Forwarding Services",
                "Use Professional Address Across All Platforms Effectively",
                "Access Meeting Rooms as Per Requirements",
                "No Hidden Charges"
              ]}
            />

            {/* Business Address Plan */}
            <PricingCard 
              icon={
                <img src="./images-optimized/galery-images/3.png" alt="Business Address" className="w-12 h-12 object-contain" />
              }
              title="Business Address Plan"
              price="₹ 7,999"
              originalPrice="₹ 5,999"
              period="/per year + GST"
              discountText="(when you sign up for 2 years)"
              buttonText="Choose Plans"
              features={[
                "Ideal for: Basic business address needs",
                "Courier Receiving and Forwarding Services",
                "Use Professional Address Across All Platforms Effectively",
                "Access Meeting Rooms as Per Requirements",
                "No Hidden Charges"
              ]}
            />
          </div>
        </div>
      </div>

      {/* Section 5: What is a Virtual Office? */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl lg:text-4xl font-bold text-[#e30613] mb-12 text-center">
            What is a Virtual Office?
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Card 1 */}
            <div className="bg-white rounded-lg p-8 shadow-sm hover:shadow-md transition-shadow">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Prestigious Business Address
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Get your business registered in one of Bangalore's most sought-after commercial areas. A verified business address enhances your company's credibility with clients, partners, and government authorities.
              </p>
            </div>

            {/* Card 2 */}
            <div className="bg-white rounded-lg p-8 shadow-sm hover:shadow-md transition-shadow">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                GST & Business Registration Support
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Our addresses are fully compliant for GST Registration, Company Registration (ROC), Udyam/MSME Registration, and Bank Current Account Opening. We provide all required NOCs, utility bills, rental agreements, and authorization letters.
              </p>
            </div>

            {/* Card 3 */}
            <div className="bg-white rounded-lg p-8 shadow-sm hover:shadow-md transition-shadow">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Mail & Courier Handling
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Never miss important communication. Our team receives and stores your business mail safely, notifies you immediately upon receipt, and forwards packages or documents to your preferred address.
              </p>
            </div>

            {/* Card 4 */}
            <div className="bg-white rounded-lg p-8 shadow-sm hover:shadow-md transition-shadow">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Access to Meeting Rooms & Workspaces
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Need to meet clients or conduct interviews? Access our meeting rooms, discussion areas, and coworking zones at discounted member rates with free meeting hours each month on selected plans.
              </p>
            </div>

            {/* Card 5 */}
            <div className="bg-white rounded-lg p-8 shadow-sm hover:shadow-md transition-shadow">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Flexible Plans & Affordable Pricing
              </h3>
              <p className="text-gray-600 leading-relaxed">
                No long-term commitments or heavy deposits. Choose monthly, quarterly, or yearly plans that fit your budget and business scale. Upgrade or cancel anytime — simple and transparent.
              </p>
            </div>

            {/* Card 6 */}
            <div className="bg-white rounded-lg p-8 shadow-sm hover:shadow-md transition-shadow">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Professional Corporate Identity
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Ideal solution for entrepreneurs, small businesses, remote teams, and online service providers who want a corporate identity without unnecessary overhead costs.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Section 6: Who Can Benefit from a Virtual Office? */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-[#e30613] mb-4">
              Who Can Benefit from a Virtual Office?
            </h2>
            <p className="text-gray-600 text-lg max-w-4xl mx-auto">
              Our Virtual Office solutions provide professional business presence without the overhead costs of traditional office space.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Card 1 */}
            <div className="bg-gray-50 rounded-lg p-8 border-l-4 border-[#e30613]">
              <h3 className="text-2xl font-bold text-[#e30613] mb-4">
                Startups and Entrepreneurs
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Perfect for launching a new business with a professional address and complete registration support for all government compliances.
              </p>
            </div>

            {/* Card 2 */}
            <div className="bg-gray-50 rounded-lg p-8 border-l-4 border-[#e30613]">
              <h3 className="text-2xl font-bold text-[#e30613] mb-4">
                Freelancers and Consultants
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Build a professional image with a prestigious business address and mail handling services to enhance credibility with clients.
              </p>
            </div>

            {/* Card 3 */}
            <div className="bg-gray-50 rounded-lg p-8 border-l-4 border-[#e30613]">
              <h3 className="text-2xl font-bold text-[#e30613] mb-4">
                E-commerce Sellers
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Get GST-registered business addresses with full compliance support for online business operations and marketplace registrations.
              </p>
            </div>

            {/* Card 4 */}
            <div className="bg-gray-50 rounded-lg p-8 border-l-4 border-[#e30613]">
              <h3 className="text-2xl font-bold text-[#e30613] mb-4">
                Companies Expanding to New Cities
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Establish presence in new markets without opening physical branches, with full registration and operational support.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-[#e30613] to-[#c1050f] py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h3 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            Ready to Establish Your Virtual Office?
          </h3>
          <p className="text-xl text-white opacity-95 mb-8">
            Get started with a professional business address today. Our team will help you choose the perfect plan for your needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => navigate('/contact')}
              className="bg-white text-[#e30613] hover:bg-gray-100 font-bold px-8 py-6 text-lg"
            >
              Get Started Now
            </Button>
            <Button 
              onClick={() => navigate('/contact')}
              variant="outline"
              className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#e30613] font-bold px-8 py-6 text-lg"
            >
              Schedule a Consultation
            </Button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default VirtualOfficePage;